/**
 * LessonListSidebar
 *
 * Learner-facing curriculum panel on the course player.
 *
 * Design intent (vs. the admin curriculum builder it shares a palette
 * with): on the learner side every row is a "next thing to do," so the
 * panel reads as a single flush list — hair dividers between rows, no
 * per-row borders. Selection is a 3px primary-color left bar plus a
 * tinted background, not a full outline; that keeps the icon chip and
 * the trailing completion mark from competing for attention.
 *
 * Modules render as quiet section headers — an uppercase eyebrow with
 * the index and complete count, then the module title in bold — rather
 * than bordered cards. That way "modules on" and "modules off" produce
 * the same visual rhythm, with the only difference being whether the
 * section headers appear at all.
 *
 * Completion shows only as a filled green check; an empty radio circle
 * per incomplete lesson reads as visual noise at 0% progress. The
 * "currently playing" lesson gets a small pulsing dot on its icon chip
 * so a returning tab can be scanned at a glance.
 *
 * Admin affordances (drag handles, edit/delete on modules) still light
 * up when `canBypass` is true.
 */

import { useEffect, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { EnrollmentStatus } from '../../apis/api-client';
import {
  CheckCircleIcon,
  DeleteIcon,
  DragIndicatorIcon,
  EditIcon,
  LockOutlinedIcon,
  RadioButtonUncheckedIcon,
} from '../icons';
import { Box, IconButton, LinearProgress, Tooltip, Typography } from '../ui';
import AskInstructorButton from '../courses/AskInstructorButton';
import { lessonTypeMeta } from '../courses/contentTypeMeta';
import { getGateLockedLessonIds } from '../../utils/lessonGates';
import { orderLessonsByCurriculum } from '../../utils/lessonOrder';

// Soft hover used across the admin curriculum builder and lesson preview.
const PASTEL_HOVER = '#f0f9ff';
// Selected-row tint and the matching left-bar accent. Pulled out so the
// "you are here" colour stays consistent between selected, hover-on-
// selected, and the now-playing dot.
const SELECTED_BG = 'rgba(var(--brand-rgb), 0.14)';
const SELECTED_BG_HOVER = 'rgba(var(--brand-rgb), 0.20)';
const ACCENT = 'rgb(var(--brand-rgb))';

interface LessonListSidebarProps {
  /** Shown as the sidebar header. Falls back to "Course Curriculum"
   *  when the consumer doesn't have a title (e.g., shell loading state). */
  courseTitle?: string;
  /** Instructor messaging config off the loaded course — surfaces the
   *  "Ask a question about this course" footer when enabled with an email. */
  instructorMessagingEnabled?: boolean;
  instructorMessagingEmail?: string;
  /** Pre-fills the learner's name in the question email body. */
  learnerName?: string;
  lessons: any[];
  selectedLessonId: number | null;
  onSelectLesson: (lesson: any) => void;
  enrollmentStatus?: EnrollmentStatus;
  progressPercentage?: number;
  completedLessonIds?: number[];
  enforceLinearProgress?: boolean;
  canBypass?: boolean;
  onReorder?: (lessonIds: number[]) => void;
  // Module support
  modules?: any[];
  onReorderModules?: (moduleIds: number[]) => void;
  onEditModule?: (module: any) => void;
  onDeleteModule?: (moduleId: number) => void;
  onAddModule?: () => void;
  /** Suppress module headers when modules mostly hold one lesson and the
   *  module name would just duplicate the lesson title. Default false. */
  showModuleHeaders?: boolean;
  /** Non-enrolled marketing view — curriculum visible for scanning but
   *  every lesson renders locked and non-clickable. */
  locked?: boolean;
}


interface LessonRowProps {
  lesson: any;
  isSelected: boolean;
  isLocked: boolean;
  isCompleted: boolean;
  canBypass: boolean;
  showDivider: boolean;
  onSelectLesson: (lesson: any) => void;
}

function SortableLessonRow({
  lesson,
  isSelected,
  isLocked,
  isCompleted,
  canBypass,
  showDivider,
  onSelectLesson,
}: LessonRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `lesson-${lesson.id}`,
    disabled: !canBypass,
  });

  const meta = lessonTypeMeta(lesson.lessonType);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      onClick={() => !isLocked && onSelectLesson(lesson)}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        pl: 1.5,
        pr: 1.25,
        py: 1,
        cursor: isLocked ? 'not-allowed' : 'pointer',
        bgcolor: isSelected ? SELECTED_BG : 'transparent',
        opacity: isLocked && !isSelected ? 0.6 : 1,
        // Hair divider between rows so the list reads as one rhythm
        // rather than a stack of cards. Suppressed on the last row
        // (or when the next row is a selected row — the selection tint
        // already provides visual separation).
        borderBottom: showDivider ? '1px solid' : 'none',
        borderBottomColor: 'divider',
        transition: 'background-color 120ms ease',
        '&:hover': {
          bgcolor: isLocked ? 'transparent' : isSelected ? SELECTED_BG_HOVER : PASTEL_HOVER,
        },
        // Left-bar accent — the only "selected" affordance besides the
        // tint. Renders inside the row so the row width stays constant
        // between selected and non-selected states.
        '&::before': isSelected
          ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 3,
              bgcolor: ACCENT,
            }
          : undefined,
      }}
    >
      {/* Drag handle — admin only. Sits flush so the rest of the
          layout doesn't shift between admin/learner views. */}
      {canBypass && (
        <Box
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            color: 'text.disabled',
            ml: -0.5,
            ':active': { cursor: 'grabbing' },
            '&:hover': { color: 'text.secondary' },
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 18 }} />
        </Box>
      )}

      {/* Type icon — colored chip. Lock overlays it when gated.
          When the lesson is the currently-selected one and not yet
          completed, a small pulsing dot appears at the top-right of
          the chip as a "now playing / now open" affordance. */}
      <Box
        sx={{
          position: 'relative',
          width: 32,
          height: 32,
          borderRadius: 1,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: isLocked ? 'grey.200' : `${meta.color}15`,
          color: isLocked ? 'grey.500' : meta.color,
        }}
      >
        {isLocked ? <LockOutlinedIcon sx={{ fontSize: 18 }} /> : meta.icon}
        {isSelected && !isLocked && !isCompleted && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: -2,
              right: -2,
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: ACCENT,
              boxShadow: '0 0 0 2px #fff',
              animation: 'lmsPulse 1.6s ease-in-out infinite',
              '@keyframes lmsPulse': {
                '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                '50%': { opacity: 0.55, transform: 'scale(0.85)' },
              },
            }}
          />
        )}
      </Box>

      {/* Title — wraps to two lines before truncating so longer lesson
          names stay legible without overflowing the panel. The type-
          coded icon on the left already conveys lesson type, so the
          redundant "Video / Quiz / …" caption was dropped. */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          fontWeight={isSelected ? 700 : 500}
          sx={{
            lineHeight: 1.3,
            color: isCompleted && !isSelected ? 'text.secondary' : 'text.primary',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}
        >
          {lesson?.title || 'Untitled lesson'}
        </Typography>
      </Box>

      {/* Completion mark — filled green check for done, hollow gray
          circle for not-yet-done. The empty state is a real "to do"
          indicator rather than negative space, which matches the
          mental model learners have when scanning the list. */}
      {isCompleted ? (
        <Tooltip title="Completed">
          <CheckCircleIcon sx={{ fontSize: 22, color: 'success.main', flexShrink: 0 }} />
        </Tooltip>
      ) : (
        <RadioButtonUncheckedIcon
          sx={{ fontSize: 20, color: 'grey.400', flexShrink: 0 }}
        />
      )}
    </Box>
  );
}

interface SortableModuleHeaderProps {
  module: any;
  moduleIndex: number;
  canBypass: boolean;
  completedCount: number;
  totalCount: number;
  showProgress: boolean;
  isFirst: boolean;
  onEdit?: (module: any) => void;
  onDelete?: (moduleId: number) => void;
}

function SortableModuleHeader({
  module,
  moduleIndex,
  canBypass,
  completedCount,
  totalCount,
  showProgress,
  isFirst,
  onEdit,
  onDelete,
}: SortableModuleHeaderProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `module-${module.id}`,
    disabled: !canBypass,
  });
  const isFullyDone = showProgress && totalCount > 0 && completedCount === totalCount;

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.5,
        pt: isFirst ? 1.25 : 2.25,
        pb: 0.75,
        // Hair divider above non-first modules so they read as a fresh
        // section without needing a card box.
        borderTop: isFirst ? 'none' : '1px solid',
        borderTopColor: 'divider',
      }}
    >
      {canBypass && (
        <Box
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            color: 'text.disabled',
            ml: -0.5,
            ':active': { cursor: 'grabbing' },
            '&:hover': { color: 'text.secondary' },
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 16 }} />
        </Box>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Eyebrow: "MODULE 2 · 1/3" — gives the section both a numeric
            anchor and an inline progress glance without a second row. */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontWeight: 700,
            fontSize: '0.65rem',
            color: isFullyDone ? 'success.main' : 'text.secondary',
            lineHeight: 1.2,
          }}
        >
          Module {moduleIndex + 1}
          {showProgress && totalCount > 0 && (
            <Box component="span" sx={{ ml: 0.75, color: 'text.disabled', fontWeight: 600 }}>
              · {completedCount}/{totalCount}
              {isFullyDone ? ' ✓' : ''}
            </Box>
          )}
        </Typography>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          noWrap
          sx={{ lineHeight: 1.3, mt: 0.125 }}
        >
          {module.title || 'Untitled module'}
        </Typography>
      </Box>

      {canBypass && (
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <IconButton size="small" onClick={() => onEdit?.(module)}>
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete?.(module.id)}>
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}

export default function LessonListSidebar({
  courseTitle,
  instructorMessagingEnabled,
  instructorMessagingEmail,
  learnerName,
  lessons,
  selectedLessonId,
  onSelectLesson,
  enrollmentStatus,
  progressPercentage,
  completedLessonIds = [],
  enforceLinearProgress = false,
  canBypass = false,
  onReorder,
  modules = [],
  onReorderModules,
  onEditModule,
  onDeleteModule,
  showModuleHeaders = false,
  locked = false,
}: LessonListSidebarProps) {
  const [localLessons, setLocalLessons] = useState(lessons);
  const [localModules, setLocalModules] = useState(modules);

  // locked = anon/non-enrolled marketing view. Suppress completion +
  // enrollment chrome so stale state can't bleed through.
  const showProgress = !locked && enrollmentStatus !== undefined;
  const effectiveCompletedIds = locked ? [] : completedLessonIds;

  useEffect(() => setLocalLessons(lessons), [lessons]);
  useEffect(() => setLocalModules(modules), [modules]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const ungroupedLessons = localLessons.filter((l) => l.moduleId == null);
  const lessonsByModule = localModules.map((module) => ({
    module,
    lessons: localLessons.filter((l) => l.moduleId === module.id),
  }));

  // Curriculum order — ungrouped lessons first, then each module's
  // lessons in module order. This is the sequence learners progress
  // through, so it's the order both the linear-progress and per-lesson
  // gate checks reason about. Shared with the lesson player via the
  // helper so the two can't disagree on what comes next.
  const orderedLessons = orderLessonsByCurriculum(localLessons, localModules);

  // Per-lesson gates (the admin "lock" toggle). A gated lesson stays
  // locked until every lesson before it in curriculum order is complete.
  // Computed once per render from the shared helper so the sidebar and
  // the player can't drift on the rule.
  const gateLockedIds = canBypass
    ? new Set<string>()
    : getGateLockedLessonIds(orderedLessons, effectiveCompletedIds);

  // A lesson is locked when EITHER:
  //   • it's the non-enrolled marketing view (everything locked), or
  //   • linear progress is on and the immediately preceding lesson
  //     isn't complete, or
  //   • it's gated and not all preceding lessons are complete.
  // Admins (canBypass) bypass everything.
  const isLessonLocked = (lesson: any): boolean => {
    if (locked) return true;
    if (canBypass) return false;
    if (gateLockedIds.has(String(lesson.id))) return true;
    if (!enforceLinearProgress) return false;
    const lessonIndex = orderedLessons.findIndex((l) => l.id === lesson.id);
    if (lessonIndex <= 0) return false;
    const previousLesson = orderedLessons[lessonIndex - 1];
    return !completedLessonIds.includes(previousLesson?.id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeId = String(active.id);
    const overId = String(over.id);

    if (activeId.startsWith('module-') && overId.startsWith('module-')) {
      const activeModuleId = parseInt(activeId.replace('module-', ''));
      const overModuleId = parseInt(overId.replace('module-', ''));
      const oldIndex = localModules.findIndex((m) => m.id === activeModuleId);
      const newIndex = localModules.findIndex((m) => m.id === overModuleId);
      const reorderedModules = arrayMove(localModules, oldIndex, newIndex);
      setLocalModules(reorderedModules);
      onReorderModules?.(reorderedModules.map((m) => m.id));
      return;
    }
    if (activeId.startsWith('lesson-') && overId.startsWith('lesson-')) {
      const activeLessonId = parseInt(activeId.replace('lesson-', ''));
      const overLessonId = parseInt(overId.replace('lesson-', ''));
      const oldIndex = localLessons.findIndex((l) => l.id === activeLessonId);
      const newIndex = localLessons.findIndex((l) => l.id === overLessonId);
      const reorderedLessons = arrayMove(localLessons, oldIndex, newIndex);
      setLocalLessons(reorderedLessons);
      onReorder?.(reorderedLessons.map((l) => l.id));
    }
  };

  const allSortableIds = [
    ...ungroupedLessons.map((l) => `lesson-${l.id}`),
    ...lessonsByModule.flatMap(({ module, lessons: moduleLessons }) => [
      `module-${module.id}`,
      ...moduleLessons.map((l) => `lesson-${l.id}`),
    ]),
  ];

  // Aggregate progress for the top-of-sidebar meter. We take the higher
  // of (locally-computed from completedLessonIds) and (server-provided
  // progressPercentage) so a freshly-completed lesson reflects in the
  // meter the instant `fetchCompletedLessons` returns, without waiting
  // for the server's progress percentage to catch up. The server number
  // can still pull ahead — e.g., if it weights some lessons more
  // heavily than 1/N — and in that case it wins.
  const totalLessons = localLessons.length;
  const completedTotal = effectiveCompletedIds.filter((id) => localLessons.some((l) => l.id === id)).length;
  const computedPct = totalLessons > 0 ? Math.round((completedTotal / totalLessons) * 100) : 0;
  // Round the final figure: the server's progressPercentage can arrive with
  // decimals (e.g. 1/14 → 7.142857), and "7.14% complete" reads as noise.
  const displayPct = Math.round(Math.max(progressPercentage ?? 0, computedPct));

  // Flatten the entire list into render-order so we can compute "is the
  // next row also non-selected?" for the hair-divider logic. The dividers
  // suppress around the selected row to avoid a stacked line / tint edge.
  type RenderItem =
    | { kind: 'lesson'; lesson: any }
    | { kind: 'module'; module: any; moduleIndex: number; lessons: any[] };

  const renderItems: RenderItem[] = [
    ...ungroupedLessons.map<RenderItem>((lesson) => ({ kind: 'lesson', lesson })),
    ...lessonsByModule
      // Empty modules don't render for learners (would just be dead space);
      // admins still see them so they can drop lessons in.
      .filter(({ lessons: ls }) => canBypass || ls.length > 0)
      .map<RenderItem>(({ module, lessons: ls }, idx) => ({
        kind: 'module',
        module,
        moduleIndex: idx,
        lessons: ls,
      })),
  ];

  const renderLessonRow = (lesson: any, nextLesson: any | undefined) => {
    const isCompleted = effectiveCompletedIds.includes(lesson.id);
    const isLockedLesson = isLessonLocked(lesson);
    const isSelected = lesson?.id === selectedLessonId;
    // Hide the bottom divider if the next item is the selected row (its
    // tint block already separates) OR if this is the last item entirely.
    const nextIsSelected = nextLesson?.id === selectedLessonId;
    const showDivider = !isSelected && !nextIsSelected && nextLesson != null;
    return (
      <SortableLessonRow
        key={lesson.id}
        lesson={lesson}
        isSelected={isSelected}
        isLocked={isLockedLesson}
        isCompleted={isCompleted}
        canBypass={canBypass}
        showDivider={showDivider}
        onSelectLesson={onSelectLesson}
      />
    );
  };

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        minWidth: 320,
        // Sticky on scroll, but with a small `top` so the sidebar sits at
        // its natural flow position on initial render — matching the
        // top edge of the lesson viewer on the right. A larger `top`
        // would push the sidebar down past the lesson viewer when the
        // page header is shorter than the offset.
        position: 'sticky',
        top: 24,
        maxHeight: 'calc(100vh - 48px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header — course title + progress meter when enrolled. */}
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: showProgress ? 1.75 : 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{ lineHeight: 1.25, mb: showProgress ? 1.25 : 0.25 }}
        >
          {courseTitle || 'Course Curriculum'}
        </Typography>
        {showProgress && totalLessons > 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                {completedTotal} of {totalLessons} complete
              </Typography>
              <Typography variant="caption" fontWeight={700} color="primary.main">
                {displayPct}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.max(0, displayPct))}
              sx={{
                height: 8,
                borderRadius: 999,
                bgcolor: 'grey.100',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                  background:
                    'var(--brand-gradient)',
                },
              }}
            />
          </Box>
        )}
        {!showProgress && (
          <Typography variant="caption" color="text.secondary">
            {totalLessons} {totalLessons === 1 ? 'lesson' : 'lessons'}
          </Typography>
        )}
      </Box>

      {/* Scrollable curriculum list — single flush list, hair dividers
          between rows, no per-row borders. Module section headers
          appear inline when enabled. */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 0.5 }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={allSortableIds} strategy={verticalListSortingStrategy}>
            {renderItems.map((item, itemIdx) => {
              if (item.kind === 'lesson') {
                const next = renderItems[itemIdx + 1];
                const nextLesson =
                  next?.kind === 'lesson'
                    ? next.lesson
                    : next?.kind === 'module'
                    ? next.lessons[0]
                    : undefined;
                return renderLessonRow(item.lesson, nextLesson);
              }
              // Module section — header (optional) then its lessons.
              return (
                <Box
                  key={`module-${item.module.id}`}
                  // With module names off there's no header row to mark where
                  // one section ends and the next begins. Without it, a module
                  // boundary reads as *less* separated than the hair dividers
                  // between the lessons inside a module — so a section's last
                  // lesson visually attaches to the next section instead of its
                  // own. Supply the boundary rule the header would have drawn so
                  // "names off" keeps the same grouping rhythm as "names on".
                  sx={
                    !showModuleHeaders && itemIdx !== 0
                      ? { mt: 1, pt: 1, borderTop: '1px solid', borderTopColor: 'divider' }
                      : undefined
                  }
                >
                  {showModuleHeaders && (
                    <SortableModuleHeader
                      module={item.module}
                      moduleIndex={item.moduleIndex}
                      canBypass={canBypass}
                      completedCount={
                        showProgress
                          ? item.lessons.filter((l) => effectiveCompletedIds.includes(l.id)).length
                          : 0
                      }
                      totalCount={item.lessons.length}
                      showProgress={showProgress}
                      isFirst={itemIdx === 0}
                      onEdit={onEditModule}
                      onDelete={onDeleteModule}
                    />
                  )}
                  {item.lessons.length > 0 ? (
                    // Lessons inside a module render flush — no hair dividers
                    // between them. The module boundary (its header when names
                    // are on, the border above when off) is the only separator,
                    // so a module reads as one clean group rather than a stack
                    // of lined-off rows.
                    item.lessons.map((lesson) => renderLessonRow(lesson, undefined))
                  ) : (
                    // Empty-module placeholder is admin-only. The
                    // render-items filter above already excludes
                    // empty modules for learners.
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', px: 2, py: 1.5, color: 'text.disabled', fontStyle: 'italic' }}
                    >
                      No lessons in this section
                    </Typography>
                  )}
                </Box>
              );
            })}
          </SortableContext>
        </DndContext>
      </Box>

      {/* Footer — "Ask a question about this course" entry point.
          Gated on the course's instructor-messaging prefs so we don't
          render a stranded divider band on courses where the feature
          isn't configured. Pinned outside the scrollable list so it
          stays anchored at the bottom of the sticky sidebar,
          contextually grouped with the course rather than competing
          with the lesson content on the right. */}
      {(() => {
        if (!instructorMessagingEnabled || !instructorMessagingEmail) return null;
        return (
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
              px: 1.5,
              py: 1.25,
            }}
          >
            <AskInstructorButton
              enabled={instructorMessagingEnabled}
              email={instructorMessagingEmail}
              courseTitle={courseTitle}
              learnerName={learnerName}
              buttonSx={{
                width: '100%',
                justifyContent: 'flex-start',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                fontWeight: 600,
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'background.paper',
                },
              }}
            />
          </Box>
        );
      })()}
    </Box>
  );
}
